/**
 * Pure helpers for the passage-highlight (annotation) feature in
 * SHSAT Adaptive. Ported from ``Module/index.tsx`` (~lines 600-865)
 * verbatim where possible — the legacy DOM-walk + offset math is
 * load-bearing and was QA'd against the prod test, so a rewrite is
 * higher risk than a copy.
 *
 * Two responsibilities:
 *   1. ``measureSelectionOffsets`` — convert a ``window.getSelection``
 *      Range into character offsets within the passage element (works
 *      across nested KaTeX spans, paragraph badges, glossary marks).
 *   2. ``generatePassageHTML`` — given a passage DOM node + a
 *      [start, end] character range + color, build the HTML overlay
 *      that wraps just the selected substring in ``<mark>`` tags.
 *
 * State / persistence is handled by the caller (AdaptiveModule keeps
 * annotations in component state, keyed by passageId so adjacent slots
 * sharing a passage keep the same highlights).
 */

export interface PassageAnnotation {
  passageId: string;
  startIndex: number;
  endIndex: number;
  /** Mark-injected outer HTML used as the highlight overlay. */
  html: string;
  /** The plaintext within the highlighted range (for preview/debug). */
  preview: string;
  /** 'Blue' or 'Pink'; null erases. */
  color: 'Blue' | 'Pink' | null;
}


export interface SelectionOffsets {
  passageId: string;
  startIndex: number;
  endIndex: number;
}


/** Walks the passage's child nodes and computes flat character offsets
 * for the current ``window.getSelection`` range. Returns null if the
 * selection is empty or falls outside any passage element. */
export function measureSelectionOffsets(): SelectionOffsets | null {
  const selection = window.getSelection();
  if (!selection || selection.toString().length === 0 || selection.rangeCount === 0) {
    return null;
  }

  const { anchorNode, anchorOffset, focusNode, focusOffset } = selection;
  if (!anchorNode || !focusNode) return null;

  const passages = document.getElementsByClassName('digitalSHSATModule__passageTextPassage');
  const passage = Array.from(passages).find(
    (p) => p.contains(anchorNode) && p.contains(focusNode),
  ) as HTMLElement | undefined;
  if (!passage) return null;

  let startOffset = 0;
  let endOffset = 0;

  for (const childNode of Array.from(passage.childNodes)) {
    const childLength = (childNode.textContent ?? '').length;
    if (childNode === anchorNode) {
      startOffset += anchorOffset;
      break;
    } else if (childNode.contains(anchorNode)) {
      const childText = childNode.textContent ?? '';
      const anchorText = anchorNode.textContent ?? '';
      const indexInChild = childText.indexOf(anchorText);
      if (indexInChild === -1) break;
      startOffset += indexInChild + anchorOffset;
      break;
    } else {
      startOffset += childLength;
    }
  }

  for (const childNode of Array.from(passage.childNodes)) {
    const childLength = (childNode.textContent ?? '').length;
    if (childNode === focusNode) {
      endOffset += focusOffset;
      break;
    } else if (childNode.contains(focusNode)) {
      const childText = childNode.textContent ?? '';
      const focusText = focusNode.textContent ?? '';
      const indexInChild = childText.indexOf(focusText);
      if (indexInChild === -1) break;
      endOffset += indexInChild + focusOffset;
      break;
    } else {
      endOffset += childLength;
    }
  }

  if (startOffset > endOffset) {
    [startOffset, endOffset] = [endOffset, startOffset];
  }
  if (startOffset === endOffset) return null;

  return {
    passageId: passage.id.replace('passage ', ''),
    startIndex: startOffset,
    endIndex: endOffset,
  };
}


/** Walks the passage's children and produces a parallel HTML string
 * where the substring [startOffset, endOffset) is wrapped in ``<mark>``
 * tags. The output preserves the passage's structure (including KaTeX
 * spans and glossary ``<mark>`` tags from the dictionary feature)
 * because we recurse through element children rather than flattening
 * to text. */
export function generatePassageHTML(
  passage: HTMLElement,
  startOffset: number,
  endOffset: number,
): { passageHTML: string; passagePreview: string } {
  let passageHTML = '';
  let passagePreview = '';
  let lastIndex = 0;

  const addMarkTags = (node: ChildNode) => {
    const el = node as HTMLElement;
    if (el.nodeName === 'IMG') {
      passageHTML += el.outerHTML;
    } else if (el.outerHTML) {
      const { innerHTML, outerHTML } = el;
      const childOffset = outerHTML.indexOf('>') + 1;
      passageHTML += outerHTML.slice(0, childOffset);
      el.childNodes.forEach(addMarkTags);
      passageHTML += outerHTML.slice(childOffset + innerHTML.length);
    } else {
      const textContent = node.textContent ?? '';
      const len = textContent.length;
      if (startOffset >= lastIndex && endOffset <= lastIndex + len) {
        passageHTML += textContent.slice(0, startOffset - lastIndex);
        passageHTML += '<mark>';
        passageHTML += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
        passageHTML += '</mark>';
        passageHTML += textContent.slice(endOffset - lastIndex);
        passagePreview += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
      } else if (startOffset >= lastIndex && startOffset < lastIndex + len) {
        passageHTML += textContent.slice(0, startOffset - lastIndex);
        passageHTML += '<mark>';
        passageHTML += textContent.slice(startOffset - lastIndex);
        passageHTML += '</mark>';
        passagePreview += textContent.slice(startOffset - lastIndex);
      } else if (endOffset >= lastIndex && endOffset < lastIndex + len) {
        passageHTML += '<mark>';
        passageHTML += textContent.slice(0, endOffset - lastIndex);
        passageHTML += '</mark>';
        passageHTML += textContent.slice(endOffset - lastIndex);
        passagePreview += textContent.slice(0, endOffset - lastIndex);
      } else if (startOffset <= lastIndex && endOffset >= lastIndex + len) {
        passageHTML += '<mark>';
        passageHTML += textContent;
        passageHTML += '</mark>';
        passagePreview += textContent;
      } else {
        passageHTML += textContent;
      }
      lastIndex += len;
    }
  };

  passage.childNodes.forEach(addMarkTags);
  return { passageHTML, passagePreview };
}


export const ANNOTATION_COLOR_CLASS: Record<NonNullable<PassageAnnotation['color']>, string> = {
  Blue: 'blue',
  Pink: 'pink',
};
